import * as c from '@chakra-ui/react'
import AnchorLink from 'react-anchor-link-smooth-scroll'
import { TranslateOptions, translateText } from 'src/utils/translateText'

import Carousel from '../carousel'

interface HeroProps {
  banners: HeroBannerProps[]
}

interface HeroBannerProps {
  images: { image: string }[]
  title: TranslateOptions
  description: TranslateOptions
  cta: TranslateOptions
  url: string
  isAnchor: boolean
  idSection: string
  hasModal: boolean
  modalOptions: {
    title: TranslateOptions
    videoUrl: string
  }
}

export default function Hero({ banners }: HeroProps) {
  return (
    <c.Container px={['s5', 0]}>
      <Carousel data={banners || []} Content={Content} hasAutoplay />
    </c.Container>
  )
}

function Content({ data }: { data: HeroBannerProps }) {
  const getRandomBgImage = (images: HeroBannerProps['images']) => {
    if (!data.images) return null
    const randomIndex = Math.floor(Math.random() * data.images.length)
    return images[randomIndex]
  }

  const randomImages = getRandomBgImage(data.images)?.image

  return (
    <c.Flex
      position={['static', 'relative']}
      minH={['396px', '465px']}
      maxH={['470px', '465px']}
      minW="100%"
      bgImage={[null, `${process.env.REACT_APP_CDN_IMAGE}/${randomImages}`]}
      bgPosition="center"
      bgRepeat="no-repeat"
      bgSize="cover"
      borderRadius={['20px 20px 0 0', '20px']}
      direction={['column', 'row']}
    >
      <c.Image
        roundedTop="20px"
        minH="176px"
        maxH="176px"
        objectFit="cover"
        objectPosition="right"
        d={['block', 'none']}
        src={`${process.env.REACT_APP_CDN_IMAGE}/${randomImages}`}
        w="full"
        h="full"
      />
      <c.Flex
        position={['static', 'absolute']}
        top={0}
        bottom={0}
        my={[0, 'auto']}
        mx={[0, 'l2']}
        p="s5"
        h={['full', 'fit-content']}
        minHeight={['300px', '']}
        w={['full', '326px']}
        bg="black.0"
        borderRadius={['0 0 20px 20px', '12px']}
        direction="column"
        gap="s4"
      >
        <c.Heading
          size="md"
          dangerouslySetInnerHTML={{
            __html: translateText(data.title) ?? '',
          }}
        />
        <c.Text
          fontSize="t-md.0"
          dangerouslySetInnerHTML={{
            __html: translateText(data.description) ?? '',
          }}
        />

        <ButtonCta data={data} />
      </c.Flex>
    </c.Flex>
  )
}

function ButtonCta({ data }: { data: HeroBannerProps }) {
  if (data.isAnchor)
    return (
      <AnchorLink href={`#${data.idSection}`} offset="100">
        <c.Button
          variant="primary"
          mt={[0, 's4']}
          maxW="fit-content"
          borderRadius="full"
        >
          {translateText(data.cta)}
        </c.Button>
      </AnchorLink>
    )

  if (data.hasModal) return <VideoModal {...data} />

  return (
    <c.Button
      as={c.Link}
      isExternal
      href={data.url}
      variant="primary"
      mt={[0, 's4']}
      maxW="fit-content"
      borderRadius="full"
    >
      {translateText(data.cta)}
    </c.Button>
  )
}

function VideoModal(props: HeroBannerProps) {
  const { isOpen, onOpen, onClose } = c.useDisclosure()

  const [isMobile] = c.useMediaQuery('(max-width: 720px)')

  return (
    <>
      <c.Button
        variant="primary"
        mt={[0, 's4']}
        maxW="fit-content"
        borderRadius="full"
        onClick={onOpen}
      >
        {translateText(props.cta)}
      </c.Button>

      <c.Modal isOpen={isOpen} onClose={onClose} size="auto">
        <c.ModalOverlay />
        <c.ModalContent
          w={['100%', '840px']}
          borderRadius="1.25rem"
          p="s5"
          marginY="auto"
          marginX={['s5', '0']}
        >
          <c.ModalCloseButton
            top={4}
            right={4}
            color="black.500"
            onClick={onClose}
            w={['1.5rem', '2rem']}
            h={['1.5rem', '2rem']}
          />

          <c.ModalBody p="0">
            <c.Heading fontSize={['h-sm', 'h-sm.1']} mb="s5">
              {translateText(props.modalOptions?.title)}
            </c.Heading>

            <iframe
              width={isMobile ? '100%' : '800'}
              height={isMobile ? '175' : '450'}
              src={props.modalOptions?.videoUrl}
              title="YouTube video player"
              allow=" autoplay; encrypted-media; picture-in-picture;"
              allowFullScreen
            ></iframe>
          </c.ModalBody>
        </c.ModalContent>
      </c.Modal>
    </>
  )
}
